<template>
  <!-- Las dos fechas van juntas bajo "Saldo al": sueltas no se entendía que
       eran un mismo filtro (ticket #437). -->
  <div
    class="balance-date-range"
    role="group"
    :aria-label="$t('contactBalanceAsOf')"
  >
    <span class="balance-date-range__title">{{
      $t("contactBalanceAsOf")
    }}</span>
    <!-- Sin rótulos visibles: la primera fecha es Desde y la segunda Hasta
         (lo dicen el lector de pantalla y el tooltip). -->
    <input
      type="date"
      class="form-control form-control-sm py-1 balance-date-range__date"
      :class="{ 'is-invalid': isRangeInvalid }"
      v-model="draftFrom"
      :aria-label="$t('contactBalanceFrom')"
      :title="isRangeInvalid ? $t('balanceDateRangeInvalid') : fromTitle"
      :disabled="loading"
      @change="scheduleApply"
    />
    <input
      type="date"
      class="form-control form-control-sm py-1 balance-date-range__date"
      :class="{ 'is-invalid': isRangeInvalid }"
      v-model="draftTo"
      :aria-label="$t('contactBalanceTo')"
      :title="isRangeInvalid ? $t('balanceDateRangeInvalid') : toTitle"
      :disabled="loading"
      @change="scheduleApply"
    />
    <span v-if="loading" class="balance-date-range__status">
      <i class="fas fa-spinner fa-spin"></i>
    </span>
  </div>
</template>

<script>
/** Espera tras elegir una fecha: si se cargan las dos seguidas, se busca una vez. */
const APPLY_DELAY_MS = 600;

/**
 * Filtro "Saldo al" con rango Desde / Hasta, sin botón: cada fecha se aplica
 * sola al elegirla (con una espera corta). Se usa con `.sync`:
 * `<BalanceDateRangeFilter :from.sync="desde" :to.sync="hasta" @change="recargar" />`.
 * `change` sale después de actualizar las dos fechas, así el padre ya las
 * tiene. Un rango al revés (Desde después de Hasta) no se aplica.
 */
export default {
  name: "BalanceDateRangeFilter",
  props: {
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    fromTitle: { type: String, default: "" },
    toTitle: { type: String, default: "" },
  },
  data() {
    return {
      draftFrom: this.from || "",
      draftTo: this.to || "",
      applyTimer: null,
    };
  },
  computed: {
    isDirty() {
      return (
        (this.draftFrom || "") !== (this.from || "") ||
        (this.draftTo || "") !== (this.to || "")
      );
    },
    /** Fechas ISO (aaaa-mm-dd): se comparan como texto. */
    isRangeInvalid() {
      return Boolean(
        this.draftFrom && this.draftTo && this.draftFrom > this.draftTo
      );
    },
  },
  watch: {
    // Si el padre cambia o limpia las fechas, el borrador las sigue.
    from(value) {
      this.draftFrom = value || "";
    },
    to(value) {
      this.draftTo = value || "";
    },
  },
  beforeDestroy() {
    if (this.applyTimer) clearTimeout(this.applyTimer);
  },
  methods: {
    scheduleApply() {
      if (this.applyTimer) clearTimeout(this.applyTimer);
      this.applyTimer = setTimeout(() => {
        this.applyTimer = null;
        this.apply();
      }, APPLY_DELAY_MS);
    },
    apply() {
      if (this.loading || !this.isDirty || this.isRangeInvalid) return;
      this.$emit("update:from", this.draftFrom || "");
      this.$emit("update:to", this.draftTo || "");
      this.$emit("change");
    },
  },
};
</script>

<style scoped>
.balance-date-range {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
}

.balance-date-range__title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.balance-date-range__date {
  width: 140px;
}

.balance-date-range__status {
  font-size: 0.8rem;
  line-height: 1;
}
</style>
